import { createSelector } from "reselect";

import { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import { BettingState } from "@ppb/betslip-core";
import { getSportsbookBettingLegs } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../store/state/sportsbook-betting/betting-resolver";
import { getSportsbookBettingImplyRunnerFailures } from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import {
  getBetslipGroup,
  getSportsbookConfirmationLegs,
} from "../../store/state/betslip-module/betslip-module-selectors";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { RunnersMetadata } from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { formatTime } from "@ppb/formatters";
import type { BetslipLocalization } from "../../contract";
import { buildHintGroups, type HintGroup } from "../../helpers/hint-groups";
import { getAvailabilityLegHint, LegHintKind } from "../../helpers/leg-hint";

export type EventGroup = {
  urn: string;
  title: string;
  legIds: string[];
  legHints: HintGroup[];
};
export type EventGroupMap = {
  [eventUrn: string]: EventGroup;
};

type LegSelector = (state: BetslipApplicationState) => BettingState.LegsMap;
type RunnerFailuresSelector = (state: BetslipApplicationState) => BettingState.ImplyRunnerFailuresMap;
type EventGroupWithoutHints = Omit<EventGroup, "legHints">;
type EventGroupWithoutHintsMap = Record<string, EventGroupWithoutHints>;

const EMPTY_RUNNER_FAILURES: BettingState.ImplyRunnerFailuresMap = {};

const hasAvailabilityHint = ({ legHints }: EventGroup): boolean =>
  legHints.some(({ kind }) => kind === LegHintKind.MarketClosed || kind === LegHintKind.MarketSuspended);

const createGetEventGroupedLegs = (legSelector: LegSelector, runnerFailuresSelector?: RunnerFailuresSelector) =>
  createSelector(
    [
      (_: ApplicationState, legIds: string[]) => legIds,
      legSelector,
      (_state: ApplicationState, _legIds: string[], localization: BetslipLocalization) => localization,
      (state: BetslipApplicationState) => getBettingResolvers(getBetslipGroup(state)).getMetadata(state),
      (state: BetslipApplicationState) => runnerFailuresSelector?.(state) ?? EMPTY_RUNNER_FAILURES,
    ],
    (
      legIds: string[],
      legs: BettingState.LegsMap,
      localization: BetslipLocalization,
      metadata: RunnersMetadata,
      runnerFailures: BettingState.ImplyRunnerFailuresMap,
    ) => {
      const eventGroups = legIds.reduce((finalEventGroupMap: EventGroupWithoutHintsMap, legId) => {
        const currentLeg = legs[legId];
        const [anyRunnerId] = currentLeg?.runners ?? [];
        const runnerMetadata = metadata[anyRunnerId];

        if (runnerMetadata.type === "RACING") {
          const { localeCodeBcp47, timezone } = localization;
          const { urn, time, venue } = runnerMetadata.racing;
          const currentEventGroup = finalEventGroupMap[urn] || { legIds: [] };

          return {
            ...finalEventGroupMap,
            [urn]: {
              urn,
              title: `${formatTime(time, localeCodeBcp47, timezone)} ${venue}`,
              legIds: [...currentEventGroup.legIds, legId],
            },
          };
        }

        const { eventUrn, eventName } = runnerMetadata;
        const currentEventGroup = finalEventGroupMap[eventUrn] || { legIds: [] };

        return {
          ...finalEventGroupMap,
          [eventUrn]: {
            urn: eventUrn,
            title: eventName,
            legIds: [...currentEventGroup.legIds, legId],
          },
        };
      }, {});

      const eventGroupEntries = Object.entries(eventGroups).map(([urn, group]): [string, EventGroup] => [
        urn,
        {
          ...group,
          legHints: buildHintGroups(
            group.legIds.map((legId) => {
              const runnerId = legs[legId]?.runners?.[0];

              return {
                legId,
                hint: getAvailabilityLegHint((runnerId && runnerFailures[runnerId]) || []),
              };
            }),
          ),
        },
      ]);

      return Object.fromEntries([
        ...eventGroupEntries.filter(([, group]) => hasAvailabilityHint(group)),
        ...eventGroupEntries.filter(([, group]) => !hasAvailabilityHint(group)),
      ]);
    },
  );

export const createGetBettingEventGroupedLegs = (): ReturnType<typeof createGetEventGroupedLegs> =>
  createGetEventGroupedLegs(getSportsbookBettingLegs, getSportsbookBettingImplyRunnerFailures);

export const createGetConfirmationEventGroupedLegs = (): ReturnType<typeof createGetEventGroupedLegs> =>
  createGetEventGroupedLegs(getSportsbookConfirmationLegs);
